import { useState } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setStatus("success");
      setEmail("");
      return;
    }
    setStatus("loading");
    try {
      const formData = new URLSearchParams();
      formData.append("email", email);
      await fetch("https://readdy.ai/api/form/d8vtques51g94qljs2rg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
      setStatus("success");
      setEmail("");
    } catch {
      setStatus("error");
    }
  };

  const firstColCats = categories.slice(0, 5);
  const secondColCats = categories.slice(5, 10);
  const thirdColCats = categories.slice(10);

  return (
    <footer className="bg-background-100 border-t border-background-200/70">
      <div className="w-full max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="flex flex-col lg:flex-row gap-12">
          <div className="lg:w-[40%]">
            <h3 className="font-heading text-2xl font-bold text-foreground-950">
              Itaobuy
              <br />
              Spreadsheet Tool
            </h3>
            <p className="mt-4 text-sm text-foreground-700 leading-relaxed max-w-sm">
              The smartest China product sourcing spreadsheet for eCommerce sellers and dropshippers.
              Discover trending products, verified supplier insights, and structured sourcing data.
            </p>
            <div className="mt-8">
              <form data-readdy-form onSubmit={handleSubscribe}>
                <label className="text-xs text-foreground-600 font-medium">Stay Updated</label>
                <div className="honeypot-wrap">
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                </div>
                <div className="mt-2 flex flex-col sm:flex-row gap-2">
                  <input
                    type="email"
                    name="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="flex-1 bg-transparent border-0 border-b border-foreground-300 px-1 py-2 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                  />
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="px-5 py-2 rounded-md border border-foreground-300 text-sm font-medium text-foreground-700 hover:border-foreground-950 hover:text-foreground-950 transition-colors whitespace-nowrap disabled:opacity-50"
                  >
                    {status === "loading" ? "Sending..." : status === "success" ? "Subscribed!" : "Subscribe"}
                  </button>
                </div>
                {status === "error" && (
                  <p className="mt-2 text-xs text-red-500">Something went wrong. Please try again.</p>
                )}
              </form>
            </div>
          </div>

          <div className="lg:w-[60%] grid grid-cols-2 md:grid-cols-3 gap-8">
            <div>
              <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-4">
                Categories
              </h4>
              <ul className="space-y-2.5">
                {firstColCats.map((cat, idx) => (
                  <li key={idx}>
                    <a
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-4">
                More Categories
              </h4>
              <ul className="space-y-2.5">
                {secondColCats.map((cat, idx) => (
                  <li key={idx}>
                    <a
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-4">
                Links
              </h4>
              <ul className="space-y-2.5">
                {thirdColCats.map((cat, idx) => (
                  <li key={idx}>
                    <a
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
                <li className="pt-2 border-t border-background-200/70">
                  <Link
                    to="/shopping-guide"
                    className="text-sm text-primary-600 hover:text-primary-700 transition-colors font-medium"
                  >
                    Shopping Guide
                  </Link>
                </li>
                <li>
                  <Link
                    to="/itaobuyspreadsheet-guide"
                    className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors"
                  >
                    Complete Guide
                  </Link>
                </li>
                <li>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors"
                  >
                    itaobuy sheet
                  </a>
                </li>
                <li>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors"
                  >
                    buy it now
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-background-200/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-500">
            &copy; {new Date().getFullYear()} itaobuy.cfd — China Sourcing Spreadsheet Tool
          </p>
          <p className="text-xs text-foreground-500">
            External links open at{" "}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-primary-600 hover:text-primary-700"
            >
              kaspreadsheet.com
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}